import React from 'react';
import { Stack } from '@fiverr-private/layout_components';
import { Typography, Text } from '@fiverr-private/typography';
import { I18n } from '@fiverr-private/i18n-react';
import { Truncate, useMonitoring, useSellerData, FederatedSeller } from '@fiverr-private/seller_pages_toolbox';
import { useIsHighlightsSectionVisible } from '../../hooks/useIsHighlightsSectionVisible';
import { useYearsOnFiverr } from '../../hooks/useYearsOnFiverr';
import { useSellerRole } from '../../hooks/useSellerRole';
import { sanitize } from '../../utils/sanitize';

interface DescriptionProps {
    sellerRoleId?: string;
}

export const Description: React.FC<DescriptionProps> = ({ sellerRoleId }) => {
    const { Providers, fireEvents } = useMonitoring();
    const { description } = useSellerData(selectSellerSlice);
    const isHighlightsSectionVisible = useIsHighlightsSectionVisible();
    const yearsOnFiverr = useYearsOnFiverr(sellerRoleId);
    const sellerRole = useSellerRole(sellerRoleId);

    if ((sellerRole && !sellerRole.description) || !description) {
        return null;
    }

    const onReadMoreBI = () => {
        fireEvents({
            [Providers.Mixpanel]: {
                type: 'seller page - description - click read more',
            },
        });
    };

    let yearsOnFiverrCopy: React.ReactNode;

    if (yearsOnFiverr < 1) {
        yearsOnFiverrCopy = (
            <I18n k="seller_page_perseus.professions_page.main_content.years_on_fiverr.less_than_year" />
        );
    } else {
        yearsOnFiverrCopy = (
            <I18n
                k={`seller_page_perseus.professions_page.main_content.years_on_fiverr.${
                    yearsOnFiverr === 1 ? 'one' : 'many'
                }`}
                params={{ count: yearsOnFiverr }}
            />
        );
    }

    const showTitle = !sellerRole;

    return (
        <Stack direction="column" gap="2">
            {showTitle && (
                <Typography size={{ default: 'h_sm', lg: 'h_md' }} fontWeight="bold" color="grey_1200">
                    <I18n k="seller_page_perseus.professions_page.main_content.about" />
                </Typography>
            )}
            {!isHighlightsSectionVisible && (
                <Text size="b_md" fontWeight="bold">
                    {yearsOnFiverrCopy}
                </Text>
            )}
            <Text size={{ default: 'b_sm', sm: 'b_md' }}>
                <Truncate lines={3} withReadLess initiallyTruncated={false} onReadMore={onReadMoreBI}>
                    <div>
                        {sellerRole ? (
                            <div dangerouslySetInnerHTML={{ __html: sanitize(sellerRole.description || '') }} />
                        ) : (
                            <div>{description}</div>
                        )}
                    </div>
                </Truncate>
            </Text>
        </Stack>
    );
};

function selectSellerSlice(seller: FederatedSeller) {
    return {
        description: seller.description,
    };
}
